import type { AgentTool, MCPServer } from "@/schemas/agent.schema";

// Source of truth is the server catalog (GET /agents/internal-tools); this set
// mirrors the catalog's alwaysAvailable flags and must be updated when they change.
// browser is included: the server resolves it to web_search (always available),
// so it must never count as deferred. code_interpreter stays out:
// alwaysAvailable false in the catalog.
export const ALWAYS_AVAILABLE_TOOL_NAMES = new Set([
  "current_datetime",
  "web_search",
  "web_fetch",
  "calculator",
  "browser",
]);

// Deprecated internal tools that the server still accepts but resolves to a
// canonical tool. Counting both spellings would inflate the total, so the
// status resolves them before counting.
const DEPRECATED_INTERNAL_ALIASES: Record<string, string> = {
  browser: "web_search",
  code_interpreter: "code_execution",
};

export function canonicalInternalToolName(name: string): string {
  return DEPRECATED_INTERNAL_ALIASES[name] ?? name;
}

export const DEFAULT_TOOL_SEARCH_THRESHOLD = 10;

interface ToolSearchStatus {
  isDeferredMode: boolean;
  totalStructuredTools: number;
  deferredToolCount: number;
  alwaysAvailableCount: number;
  /** Enabled structured tools by origin, so the UI can explain the count. */
  byOrigin: { internal: number; api: number; knowledge: number; mcp: number };
}

export function computeToolSearchStatus(agent: {
  /** Snapshots antigos e agentes criados fora da UI chegam com `null`. */
  tools?: AgentTool[] | null;
  mcp_servers?: MCPServer[];
  settings?: Record<string, unknown> | null;
}): ToolSearchStatus {
  const settings = agent.settings ?? {};
  const toolSearchEnabled = settings.tool_search_enabled !== false;
  const threshold = (typeof settings.tool_search_threshold === "number"
    ? settings.tool_search_threshold
    : DEFAULT_TOOL_SEARCH_THRESHOLD);

  const nonMcpTools = (agent.tools ?? []).filter(
    t => !t.disabled && t.type !== "function_call" && t.type !== "mcp",
  );

  let mcpToolCount = 0;
  for (const server of agent.mcp_servers ?? []) {
    if (server.disabled) continue;
    mcpToolCount += server.tools?.filter(t => !t.disabled).length ?? 0;
  }

  // Deprecated aliases resolve to the same canonical tool (browser -> web_search,
  // code_interpreter -> code_execution), so they must be counted once.
  const internalNames = new Set(
    nonMcpTools.filter(t => t.type === "internal").map(t => canonicalInternalToolName(t.tool.name)),
  );
  const internalCount = internalNames.size;
  const apiCount = nonMcpTools.filter(t => t.type === "api").length;
  const knowledgeCount = nonMcpTools.filter(
    t => t.type === "rag" || t.type === "knowledge",
  ).length;
  const totalStructuredTools = internalCount + apiCount + knowledgeCount + mcpToolCount;

  const isDeferredMode = toolSearchEnabled && totalStructuredTools > threshold;

  const alwaysAvailableCount = isDeferredMode
    ? [...internalNames].filter(name => ALWAYS_AVAILABLE_TOOL_NAMES.has(name)).length
    : 0;

  const deferredToolCount = isDeferredMode
    ? totalStructuredTools - alwaysAvailableCount
    : 0;

  return {
    isDeferredMode,
    totalStructuredTools,
    deferredToolCount,
    alwaysAvailableCount,
    byOrigin: {
      internal: internalCount,
      api: apiCount,
      knowledge: knowledgeCount,
      mcp: mcpToolCount,
    },
  };
}

export function isNeverDeferredTool(tool: AgentTool): boolean {
  if (tool.type === "function_call") return true;
  return tool.type === "internal" && ALWAYS_AVAILABLE_TOOL_NAMES.has(tool.tool.name);
}
